import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  Input,
  Select,
  Checkbox,
  RadioButton,
  TextArea,
  PhoneInput,
  InitialsInput,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function OHTFCF084() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyName, setFamilyName] = useState("");
  const [familyPhone, setFamilyPhone] = useState("");
  const [familyAddress, setFamilyAddress] = useState("");

  const handleAddFamilyMember = () => {
    const obj = { ...data };
    const familyMembersData = [...familyMembers];
    familyMembersData.push({
      Name: familyName,
      PhoneNumber: familyPhone,
      Address: familyAddress,
    });
    setFamilyMembers(familyMembersData);
    obj.FamilyInfo["@value"] = JSON.stringify(familyMembersData);
    setData(obj);
    setFamilyName("");
    setFamilyPhone("");
    setFamilyAddress("");
  };

  const yesNoOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <Checkbox
                name="DiagnosticAssessment"
                label="Psychiatric Diagnostic Assessment"
                checked={data.DiagnosticAssessment["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <InitialsInput
                name="DiagnosticAssessmentInitials"
                label="Initials"
                value={data.DiagnosticAssessmentInitials["@value"]}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <p style={{ textIndent: "30px" }}>
                I understand that Psychiatric Diagnostic Assessment, which may
                include a review of referral materials, face-to-face interviews
                with me and significant others, and use of formal assessment
                protocols, is designed to assist in the development of a clearer
                picture of my social, emotional, health, and behavioral needs. A
                potential benefit is that gathered information may be utilized
                for one or more of the following: treatment planning, social
                planning, school planning, pre-vocational and vocational
                planning, etc.
              </p>
              <p style={{ textIndent: "30px" }}>
                The possible risk associated with receiving this service may
                include increase in stress due to the focus placed on
                identifying problem areas. Additional potential risks associated
                with refusing to stop this service is that there may be
                insufficient information available for sound planning on my
                behalf. This, in turn, may limit my access to services and
                interventions aimed at facilitating my development of skills
                needed for a more adaptive way of living.
              </p>
              <Checkbox
                name="Therapy"
                label="Individual/Group/Family Psychotherapy"
                checked={data.Therapy["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <InitialsInput
                name="TherapyInitials"
                label="Initials"
                value={data.TherapyInitials["@value"]}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <p style={{ textIndent: "30px" }}>
                I understand that Psychotherapy is a shared effort and that
                success or failure is the result of the efforts of both the
                therapist and me. Specific benefits of an effective therapy for
                me are outlined in my Individualized Service Plan. General
                benefits of Psychotherapy may include relief of symptoms,
                increased understanding and confidence, improvement of
                interpersonal relationships, decreased anxiety, and a general
                improvement in my daily living.
              </p>
              <p style={{ textIndent: "30px" }}>
                Possible risks associated with Psychotherapy include a temporary
                increase in stress due to the focus put on problem areas and the
                possibility of developing dependency on the therapist.
                Additional potential risk associated with refusing or stopping
                this service may include either a continuation or a worsening of
                the symptoms and problems that led to the recommendation for
                Psychotherapy. This, in turn, may impede my development of
                skills needed for a more adaptive way of living and result in my
                admission to a more restrictive treatment environment.
              </p>
              <Checkbox
                name="CPST"
                label="Individual/Group Community Psychiatric Supportive Treatment (CPST) and Individual/Group Therapeutic Behavioral Services (TBS)"
                checked={data.CPST["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <InitialsInput
                name="CPSTInitials"
                label="Initials"
                value={data.CPSTInitials["@value"]}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <p style={{ textIndent: "30px" }}>
                I understand that CPST/TBSis designed to help me address the
                problems and goals identified on my treatment plan. My ability
                to function well enough that I am able to remain in community
                living and to participate in a community-based school program is
                a potential primary benefit.
              </p>
              <p style={{ textIndent: "30px" }}>
                The possible risks associated with refusing or stopping this
                service may include either a continuation or a worsening of the
                symptoms and problems that led to my need for CPST/TBS. This, in
                turn, may result in my need for a more intensive intervention,
                placement in a more restrictive living environment and/or
                enrollment in a more restrictive school setting.
              </p>
              <Checkbox
                name="Management"
                label="Evaluation &amp; Management Psychiatric Services"
                checked={data.Management["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <InitialsInput
                name="ManagementInitials"
                label="Initials"
                value={data.ManagementInitials["@value"]}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <p style={{ textIndent: "30px" }}>
                I understand that Evaluation &amp; Management Psychiatric
                Services is designed to assist me in managing my medical and
                psychiatric needs by offering me care that may include
                diagnostic assessments, physical examinations, prescription and
                management of medication, and other routine medical/nursing
                procedures/maintenance of optimal physical health and a
                reduction in psychiatric symptoms are potential primary benefits
                of Evaluation &amp; Management Psychiatric Services.
              </p>
              <p style={{ textIndent: "30px" }}>
                The possible risks associated with refusing or stopping this
                service may include either a continuation or a worsening of the
                symptoms andmay result in the need either for hospitalization or
                for placement in a more restrictive treatment environment.
              </p>
              <Checkbox
                name="Conferencing"
                label="Services via Secured Videoconferencing"
                checked={data.Conferencing["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <InitialsInput
                name="ConferencingInitials"
                label="Initials"
                value={data.ConferencingInitials["@value"]}
                onChange={handleChange}
                disabled={formDisabled}
              />
              <p>
                I understand that the risks of using such services include, but
                are not limited to the following:
              </p>
              <ul>
                <li>
                  Risk of misunderstanding one another when communication lacks
                  visual or auditory cues due to video quality. It may be
                  difficult to get a full read on body language, gestures, or
                  other nonverbal communication that might otherwise be easily
                  observed in the home.
                </li>
                <li>
                  I may misinterpret or not fully comprehend something said or
                  may not fully discern nonverbal communication of SAFY staff.
                </li>
                <li>
                  SAFY staff are limited in their ability to respond to imminent
                  crisis situations, and therefore other plans must be put in
                  place for such circumstances.
                </li>
                <li>
                  There could be disruption in services due to poor internet
                  speed or connection, and therefore other plans must be put in
                  place for such circumstances.
                </li>
                <li>
                  Because interactive communication through videoconferencing is
                  an electronic transmittal of information, there could be risk
                  of security breaches. To reduce this risk, SAFY will only use
                  methods of interactive videoconferencing that are secured and
                  HIPAA-compliant to assure client privacy.
                </li>
              </ul>
              <p>
                I release and discharge SAFY of Ohio, its affiliate, agents, and
                employees, and any other organization involved in the mental
                health services via secured videoconferencing from any liability
                in connection with my participation with mental health services
                via secured videoconferencing.
              </p>
              <p>
                I acknowledge to have my name and email used in coordination of
                services through a secured, HIPAA-compliant videoconferencing
                service provider and that I will assure that only I (or
                guardian/designee) will have access to my log-in information,
                which will be used as verification when logging in at the time
                of scheduled sessions. The email address that I would like to be
                used for this service is as follows:
              </p>
              <Input
                name="Email"
                label="Email:"
                error={errors.Email}
                value={data.Email["@value"]}
                onChange={handleChange}
                identical={compare("Email")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <p>
                I have read this Consent carefully and fully understand the
                risks and benefits of mental health services via secured
                videoconferencing. I have had the opportunity to ask questions
                and I have received satisfactory answers. I voluntarily consent
                to participate in the mental health services via secured
                videoconferencing, including but not limited to any care,
                treatment, and services deemed necessary and advisable, under
                the terms described herein.
              </p>
              <div className="h5">
                <u>Authorizations</u>
              </div>
              <b>
                SAFY's practice is to involve primary family members as much as
                possible.
              </b>
              <br />
              Please provide us with the following:
              <table className="table table-striped mt-2">
                <tbody>
                  {data.FamilyInfo["@value"] &&
                    JSON.parse(data.FamilyInfo["@value"]).map((item, index) => (
                      <tr key={item._id}>
                        <td>
                          <div>
                            Name: <u>{item.Name}</u>
                          </div>
                          <div>
                            Phone number: <u>{item.PhoneNumber}</u>
                          </div>
                        </td>
                        <td>
                          Address: <br />
                          <u>{item.Address}</u>
                        </td>
                      </tr>
                    ))}
                  <tr>
                    <td>
                      <Input
                        name="Name"
                        label="Name:"
                        value={familyName}
                        onChange={(e) => setFamilyName(e.currentTarget.value)}
                        disabled={formDisabled}
                      />

                      <PhoneInput
                        name="PhoneNumber"
                        label="Phone number:"
                        value={familyPhone}
                        onChange={(e) => setFamilyPhone(e.currentTarget.value)}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <TextArea
                        name="Address"
                        label="Address:"
                        rows="2"
                        value={familyAddress}
                        onChange={(e) =>
                          setFamilyAddress(e.currentTarget.value)
                        }
                        disabled={formDisabled}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              <button
                className="btn btn-primary mb-2"
                onClick={handleAddFamilyMember}
                disabled={
                  familyName === "" ||
                  familyPhone === "" ||
                  familyAddress === ""
                }
              >
                Add Family Member
              </button>
              <RadioButton
                name="ContactRestrictions"
                label="Are there restrictions regarding contact with primary family members?"
                options={yesNoOptions}
                error={errors.ContactRestrictions}
                value={data.ContactRestrictions["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="InitialEntry"
                label="Is this the client’s initial entry into care?"
                options={yesNoOptions}
                error={errors.InitialEntry}
                value={data.InitialEntry["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.InitialEntry["@value"] === "1" && (
                <>
                  <div className="row">
                    <div className="col">
                      <RadioButton
                        name="HealthScreening"
                        label="If yes, has the client received a 72-hour health screening?"
                        options={yesNoOptions}
                        error={errors.HealthScreening}
                        value={data.HealthScreening["@value"]}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.HealthScreening["@value"] === "1" && (
                        <>
                          <Input
                            name="HealthScreeningDate"
                            label="Date of Screening:"
                            error={errors.HealthScreeningDate}
                            value={data.HealthScreeningDate["@value"]}
                            onChange={handleChange}
                            identical={compare("HealthScreeningDate")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            type="date"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  {data.HealthScreening["@value"] === "1" && (
                    <p>
                      <u>
                        Please ensure SAFY receives a copy of the screening for
                        the file.
                      </u>
                    </p>
                  )}
                </>
              )}
              <RadioButton
                name="HealthConcerns"
                label="Are there any current health concerns (physical/mental/behavioral) that need immediate attention?"
                options={yesNoOptions}
                error={errors.HealthConcerns}
                value={data.HealthConcerns["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.HealthConcerns["@value"] === "1" && (
                <TextArea
                  name="HealthConcernsDetails"
                  label="If yes, identify:"
                  error={errors.HealthConcernsDetails}
                  value={data.HealthConcernsDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("HealthConcernsDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <RadioButton
                name="MentalHealthServices"
                label="Is the client receiving mental health services currently?"
                options={yesNoOptions}
                error={errors.MentalHealthServices}
                value={data.MentalHealthServices["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.MentalHealthServices["@value"] === "1" && (
                <TextArea
                  name="MentalHealthServicesDetails"
                  label="If yes, please specify:"
                  error={errors.MentalHealthServicesDetails}
                  value={data.MentalHealthServicesDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("MentalHealthServicesDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              <RadioButton
                name="Smoke"
                label="Does the client smoke?"
                options={yesNoOptions}
                error={errors.Smoke}
                value={data.Smoke["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="PermissionToSmoke"
                label="Does client have custodial agency’s permission to smoke?"
                options={yesNoOptions}
                error={errors.PermissionToSmoke}
                value={data.PermissionToSmoke["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="Unsupervised"
                label="May client be left unsupervised?"
                options={yesNoOptions}
                error={errors.Unsupervised}
                value={data.Unsupervised["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Unsupervised["@value"] === "1" && (
                <TextArea
                  name="UnsupervisedDetails"
                  label="Describe amount of time or situations where the client may be left unsupervised:"
                  error={errors.UnsupervisedDetails}
                  value={data.UnsupervisedDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("UnsupervisedDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              If applicable,supply Client’s GAL or CASA worker contact
              information:
              <Input
                name="CASAWorkerName"
                label="Name:"
                error={errors.CASAWorkerName}
                value={data.CASAWorkerName["@value"]}
                onChange={handleChange}
                identical={compare("CASAWorkerName")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="CASAWorkerPhone"
                label="Phone number:"
                error={errors.CASAWorkerPhone}
                value={data.CASAWorkerPhone["@value"]}
                onChange={handleChange}
                identical={compare("CASAWorkerPhone")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="ObtainLicense"
                label="Can client obtain driver’s permit or license?"
                options={yesNoOptions}
                error={errors.ObtainLicense}
                value={data.ObtainLicense["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="PermissionToDrive"
                label="Does client have permission to drive?"
                options={yesNoOptions}
                error={errors.PermissionToDrive}
                value={data.PermissionToDrive["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="PermissionToSleepOver"
                label="Does client have permission to spend the night with a friend?"
                options={yesNoOptions}
                error={errors.PermissionToSleepOver}
                value={data.PermissionToSleepOver["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="PermissionToRide"
                label="Is client allowed to ride with licensed drivers under the age of 18?"
                options={yesNoOptions}
                error={errors.PermissionToRide}
                value={data.PermissionToRide["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="Consent"
                label="I give my consent to SAFY to provide the treatment explained above."
                checked={data.Consent["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="PrivacyNotices"
                label="I acknowledge that I have received a copy of the Specialized Alternatives for Family and Youth Notice of Privacy Practices."
                checked={data.PrivacyNotices["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Checkbox
                name="Communication"
                label="I authorize SAFY to utilize email, voicemail and text as methods of communication regarding client care."
                checked={data.Communication["@value"] === "true"}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <br />
              <i>Revised Date: 04/14/2020</i>
              {/* 
                Consent: Joi.boolean(),
                PrivacyNotices: Joi.boolean(),
                Communication: Joi.boolean(), */}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default OHTFCF084;
